import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import * as actions from '@/store/slices/subApplication.slice'; // Import your action creators
import * as api from '@/services/subApplication'; // Import your API functions
import { PayloadAction } from '@reduxjs/toolkit';
import { isBoolean } from 'lodash';
import { IPagination } from '../types';
import { Operator, DeleteOperator } from './operatorSaga';

// Define action types
export interface GetSubapplicationAction extends PayloadAction<number> {
  type: 'subapplication/getSubApplication';
}

export interface CreateSubApplicationAction extends PayloadAction<Operator> {
  type: 'subapplication/createSubApplication';
}

export interface UpdateSubApplicationAction extends PayloadAction<Operator> {
  type: 'subapplication/updateSubApplication';
}

export interface DeleteSubApplicationAction extends PayloadAction<DeleteOperator> {
  type: 'subapplication/deleteSubApplication';
}

// Define the shape of the API response for a single provider
export interface SingleSubApplicationResponse {
  id: string;
  name: string;
  createdOn: string;
  updatedOn: string;
}

type SubapplicationProps = {
  id: string;
  name: string;
  createdOn: string;
  updatedOn: string;
};

export interface CreateSubApplicationProps {
  name: string;
}

export interface DeleteSubApplication {
  providerId: string;
}

export interface SelectedSubApplication {
  name: string;
  id: string;
}

export interface ApiResponse {
  success: boolean;
  message: string;
  data: SubapplicationProps[];
  pagination: IPagination;
}

function* getSubapplications(action: GetSubapplicationAction): Generator {
  try {
    const response: any = yield call(api.fetchsubApplications as any, action?.payload);
    if (isValidSubApplicationsResponse(response?.data)) {
      yield put(actions.getSubApplicationsSuccess(response?.data));
    } else {
      yield put(actions.getSubApplicationsFailure());
    }
  } catch (error) {
    yield put(actions.getSubApplicationsFailure());
  }
}

// Your saga function
function* getSubapplication(action: GetSubapplicationAction): Generator {
  try {
    const response: any = yield call(api.fetchsubApplication, action?.payload);

    const res = response?.data?.data;

    if (isValidProviderResponse(res)) {
      yield put({
        type: 'subapplication/getSubApplicationSuccess',
        payload: res as SingleSubApplicationResponse,
      });
    } else {
      yield put({ type: 'subapplication/getSubApplicationFailure' });
    }
  } catch (error) {
    yield put({ type: 'subapplication/getSubApplicationFailure' });
  }
}

function* createSubApplication(action: CreateSubApplicationAction) {
  try {
    const response: unknown = yield call(
      api.createSubApplication,
      action?.payload,
    );
    if (isValidProviderResponse(response)) {
      yield put({
        type: 'subapplication/createSubApplicationSuccess',
        payload: response as SingleSubApplicationResponse,
      });
    } else {
      yield put({ type: 'subapplication/createSubApplicationFailure' });
    }
  } catch (error) {
    yield put({ type: 'subapplication/createSubApplicationFailure' });
  }
}

function* updateSubApplication(action: UpdateSubApplicationAction) {
  try {
    const response: unknown = yield call(
      api.updateSubApplication,
      action?.payload?.providerId,
      action?.payload,
    );
    if (isValidProviderResponse(response)) {
      yield put({
        type: 'subapplication/updateSubApplicationSuccess',
        payload: response as SingleSubApplicationResponse,
      });
    } else {
      yield put({
        type: 'subapplication/updateSubApplicationFailure',
        payload: response,
      });
    }
  } catch (error) {
    yield put({
      type: 'subapplication/updateSubApplicationFailure',
      payload: 'Something went wrong',
    });
  }
}

function* deleteSubApplication(action: DeleteSubApplicationAction) {
  try {
    const response: unknown = yield call(
      api.deleteSubApplication as any,
      action?.payload,
    );
    if (isValidProviderResponse(response)) {
      yield put({
        type: 'subapplication/deleteSubApplicationSuccess',
        payload: response as SingleSubApplicationResponse,
      });
    } else {
      yield put({ type: 'subapplication/deleteSubApplicationFailure' });
    }
  } catch (error) {
    yield put({
      type: 'subapplication/deleteSubApplicationFailure',
      payload: 'Something went wrong',
    });
  }
}

function isValidProviderResponse(
  response: unknown,
): response is SingleSubApplicationResponse {
  return (
    typeof response === 'object' &&
    response !== null &&
    'providerId' in response &&
    'partnerId' in response &&
    'name' in response &&
    'createdOn' in response &&
    'updatedOn' in response
    // Add other properties if present in the API response
  );
}

function isValidSubApplicationsResponse(response: ApiResponse): boolean {
  return (
    isBoolean(response.success) &&
    response.message === 'Successfully got list of subapplications.'
  );
}

function* operatorSaga(): Generator {
  // Watch for the specific action type
  yield takeLatest<GetSubapplicationAction>(
    'subapplication/getSubApplications',
    getSubapplications,
  );
  yield takeEvery<GetSubapplicationAction>(
    'subapplication/getSubApplication',
    getSubapplication,
  );
  yield takeEvery<CreateSubApplicationAction>(
    'subapplication/createSubApplication',
    createSubApplication,
  );
  yield takeEvery<UpdateSubApplicationAction>(
    'subapplication/updateSubApplication',
    updateSubApplication,
  );
  yield takeEvery<DeleteSubApplicationAction>(
    'subapplication/deleteSubApplication',
    deleteSubApplication,
  );
}

export default operatorSaga;
